// Vessel Progress: where each boat of the season stands in build, read-only and sales-friendly (its own route,
// /api/vessel-progress, so Sales sees this much of the Overview and no more). Filters (the
// shared filter bar, folded behind "Filters" on phones): a search, the completion (not started, under half,
// half or more, complete), the model and the delivery (dated, confirmed, not dated yet). Every column sorts
// from its header. Filters and sort are kept for the tab.

import { useEffect, useMemo } from 'react';
import { MODEL_ORDER, assemblyPctNum, modelKey, parseDeliveryDate } from '@technohull/shared';
import { useVesselProgress, type VesselProgressRow as OverviewVessel } from '../features/hooks.js';
import { Chip, ChipGroup, EmptyState, ErrorState, FilterBar, FilterSelect, LoadingState, PageHeader, Pill, ProgressBar, SearchInput, SyncLabel } from '../components/ui.js';
import { IconCheck } from '../components/icons.js';
import { SortMark } from '../components/SortMark.js';
import { useFormatDate } from '../lib/format.js';
import { fold, hasText } from '../lib/search.js';
import { useTabState } from '../lib/tabState.js';
import { sortRows, useTableSort } from '../lib/tableSort.js';

type Stage = 'ALL' | 'none' | 'under' | 'over' | 'done';
const STAGES: Array<{ id: Exclude<Stage, 'ALL'>; label: string; title: string; test: (pct: number) => boolean }> = [
  { id: 'none', label: 'Not started', title: '0% done', test: (p) => p <= 0 },
  { id: 'under', label: 'Under half', title: '1–49% done', test: (p) => p > 0 && p < 50 },
  { id: 'over', label: 'Half or more', title: '50–99% done', test: (p) => p >= 50 && p < 100 },
  { id: 'done', label: 'Complete', title: '100% done', test: (p) => p >= 100 },
];
type Delivery = 'ALL' | 'dated' | 'confirmed' | 'none';
const DELIVERY: Array<{ value: Delivery; label: string }> = [
  { value: 'ALL', label: 'Any' },
  { value: 'dated', label: 'Has a date' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'none', label: 'No date yet' },
];
const hasDelivery = (v: OverviewVessel) => Boolean(String(v.deliveryDate ?? '').trim());
const deliveryOk = (v: OverviewVessel, d: Delivery) =>
  d === 'ALL' || (d === 'dated' ? hasDelivery(v) : d === 'confirmed' ? Boolean(v.deliveryConfirmed) : !hasDelivery(v));
/** a vessel's model group ("Alpha 40" and "40" together), its own text when it is none of the yard's */
const modelOf = (v: OverviewVessel) => modelKey(v.model) || String(v.model ?? '').trim().toUpperCase() || '—';

type Col = 'name' | 'sap' | 'model' | 'pct' | 'delivery';

export function VesselProgress() {
  const { data, isLoading, isFetching, error, refetch } = useVesselProgress();
  const fmtDate = useFormatDate();
  const [q, setQ] = useTabState('vp.q', '');
  const [stage, setStage] = useTabState<Stage>('vp.stage', 'ALL');
  const [model, setModel] = useTabState('vp.model', 'ALL');
  const [delivery, setDelivery] = useTabState<Delivery>('vp.delivery', 'ALL');
  const { sort, dirOf, th } = useTableSort<Col>('vessel-progress', { key: 'name', dir: 'asc' });

  const vessels = useMemo(() => (data?.vessels ?? []).filter((v) => !v.left), [data]);
  // the models on the list, each named as the sheet mostly writes it, in the yard's order
  const models = useMemo(() => {
    const found = new Map<string, { labels: Map<string, number>; n: number }>();
    for (const v of vessels) {
      const k = modelOf(v);
      const m = found.get(k) ?? { labels: new Map<string, number>(), n: 0 };
      const label = String(v.model ?? '').trim() || 'No model';
      m.labels.set(label, (m.labels.get(label) ?? 0) + 1);
      m.n++;
      found.set(k, m);
    }
    const rank = (k: string) => { const i = (MODEL_ORDER as readonly string[]).indexOf(k); return i < 0 ? 99 : i; };
    return [...found]
      .map(([key, m]) => ({ key, n: m.n, label: [...m.labels].sort((a, b) => b[1] - a[1])[0]![0] }))
      .sort((a, b) => rank(a.key) - rank(b.key) || a.label.localeCompare(b.label));
  }, [vessels]);
  // a model no boat has any more (another season, a Sync) falls back to all
  useEffect(() => { if (model !== 'ALL' && models.length && !models.some((m) => m.key === model)) setModel('ALL'); }, [models, model, setModel]);

  // everything but the completion, so its chips can say how many each would show
  const base = useMemo(() => {
    const needle = fold(q.trim());
    return vessels.filter((v) => (!needle || hasText(needle, v.name, v.sap, v.model)) && (model === 'ALL' || modelOf(v) === model) && deliveryOk(v, delivery));
  }, [vessels, q, model, delivery]);
  const stageCount = (id: Exclude<Stage, 'ALL'>) => base.filter((v) => STAGES.find((s) => s.id === id)!.test(assemblyPctNum(v))).length;
  const rows = useMemo(() => {
    const test = STAGES.find((s) => s.id === stage)?.test;
    const kept = test ? base.filter((v) => test(assemblyPctNum(v))) : base;
    return sortRows(kept, sort, (v, key) =>
      key === 'pct' ? assemblyPctNum(v)
        : key === 'delivery' ? (parseDeliveryDate(v)?.toISOString().slice(0, 10) ?? (String(v.deliveryDate ?? '').trim() || null))
        : key === 'model' ? v.model
        : key === 'sap' ? v.sap
        : v.name);
  }, [base, stage, sort]);

  const active = (q.trim() ? 1 : 0) + (stage !== 'ALL' ? 1 : 0) + (model !== 'ALL' ? 1 : 0) + (delivery !== 'ALL' ? 1 : 0);
  const clear = () => { setQ(''); setStage('ALL'); setModel('ALL'); setDelivery('ALL'); };

  return (
    <>
      <PageHeader title="Vessel Progress">
        <span className="muted page-count">{rows.length === vessels.length ? `${vessels.length} vessels` : `${rows.length} of ${vessels.length} vessels`}</span>
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>
          <SyncLabel busy={isFetching} />
        </button>
      </PageHeader>

      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && (
        <FilterBar activeCount={active} onClear={clear} search={<SearchInput value={q} onChange={setQ} placeholder="Search name / SAP / model" width={220} />}>
          <ChipGroup label="Completion">
            <Chip active={stage === 'ALL'} onClick={() => setStage('ALL')}>All <b className="svc-chipn">{base.length}</b></Chip>
            {STAGES.map((s) => (
              <Chip key={s.id} active={stage === s.id} title={s.title} onClick={() => setStage((p) => (p === s.id ? 'ALL' : s.id))}>
                {s.label} <b className="svc-chipn">{stageCount(s.id)}</b>
              </Chip>
            ))}
          </ChipGroup>
          {models.length > 1 && (
            <FilterSelect
              label="Model"
              value={model}
              onChange={setModel}
              options={[{ value: 'ALL', label: 'All models' }, ...models.map((m) => ({ value: m.key, label: `${m.label} · ${m.n}` }))]}
            />
          )}
          <FilterSelect label="Delivery" value={delivery} onChange={(v) => setDelivery(v as Delivery)} options={DELIVERY} />
        </FilterBar>
      )}

      {data && (
        <div className="tbl-wrap">
          {/* phones: one card per vessel (name, SAP · model, the completion bar, delivery) */}
          <table className="tbl tbl-cards">
            <thead>
              <tr>
                <th {...th('name')}>Name<SortMark dir={dirOf('name')} /></th>
                <th {...th('sap')}>SAP<SortMark dir={dirOf('sap')} /></th>
                <th {...th('model')}>Model<SortMark dir={dirOf('model')} /></th>
                <th {...th('pct')} style={{ minWidth: 160 }}>Completion<SortMark dir={dirOf('pct')} /></th>
                <th {...th('delivery')}>Delivery<SortMark dir={dirOf('delivery')} /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const pct = assemblyPctNum(v);
                return (
                  <tr key={v.rowId}>
                    <td className="tc-title">{v.name}</td>
                    <td className="tc-hide">{v.sap}</td>
                    <td className="tc-hide">{v.model}</td>
                    <td className="tc-meta vp-meta">{[v.sap, v.model].filter(Boolean).join(' · ')}</td>
                    <td className="tc-full">
                      <div className="ov-assy">
                        <ProgressBar pct={pct} done={pct >= 100} />
                        <span className="ov-assy-pct">{Math.round(pct)}%</span>
                      </div>
                    </td>
                    <td className="tc-lbl" data-label="Del">
                      {parseDeliveryDate(v) ? fmtDate(v.deliveryDate) : v.deliveryDate || '-'}{' '}
                      {v.deliveryConfirmed && <Pill kind="ok" title="Delivery confirmed"><IconCheck size={12} /><span className="sr-only">Delivery confirmed</span></Pill>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <EmptyState title="No vessels match" hint={active ? 'Try other filters, or clear them.' : 'No vessels in production.'} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
